import { CostEventObjectRef } from "src/api/generated/erp/common/costEvents/model/costEventObjectRef.ts";
import { FormCommonProps } from "../../../../common/forms/types.ts";
import { CostEvent } from "src/api/generated/erp/db/types/tables/costEvent.ts";
import {
	CostEventFormApi,
	CostEventFormApi_InitData,
} from "src/api/generated/erp/common/costEvents/api/costEventFormApi.ts";
import { AsyncForm } from "../../../../common/forms/AsyncForm.tsx";
import { useStoredState } from "src/utils/useStoredState.ts";
import { CostEventFormContent } from "src/components/views/erp/common/costEvents/CostEventFormContent.tsx";
import { getCostEventFormDefaultValues } from "src/components/views/erp/common/costEvents/CostEventFormContent.utils.ts";
import i18n from "i18next";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CostEventFormProps extends FormCommonProps<number> {
	costEventId: number | undefined;
	costEventPriceListId: number | undefined;
	newCostEventObjectRef: CostEventObjectRef | undefined;
}

export const CostEventForm = ({
	costEventId,
	costEventPriceListId,
	newCostEventObjectRef,
	onCompleted,
	onFormEdited,
}: CostEventFormProps) => {
	const [storedValues, setStoredValues] = useStoredState<{ itemId: number | undefined }>({
		key: "14275B81F9BEB4ED",
		defaultValue: {
			itemId: undefined,
		},
	});
	return (
		<AsyncForm<CostEventFormApi_InitData, CostEvent, number>
			fetch={() => CostEventFormApi.getInitData({ costEventId, costEventPriceListId })}
			getDefaultValues={({ costEvent, priceListLines }) =>
				getCostEventFormDefaultValues({
					costEvent,
					priceListLines,
					storedItemId: storedValues.itemId,
				})
			}
			submit={submit}
			columns={1}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.common.costEvent.form"),
				title: i18n.t("cost_event"),
				pathSegments: [i18n.t("form")],
				data: {
					costEventId: costEventId ?? null,
					costEventPriceListId: costEventPriceListId ?? null,
					objectType: newCostEventObjectRef?.objectType ?? null,
					objectId: newCostEventObjectRef?.objectId ?? null,
				},
			}}
			render={({ control, setValue, watch, data: initData }) => (
				<CostEventFormContent
					control={control}
					setValue={setValue}
					watch={watch}
					initData={initData}
					setStoredItemId={(itemId) => setStoredValues({ itemId })}
				/>
			)}
		/>
	);

	async function submit(costEvent: CostEvent): Promise<number> {
		if (costEventId == null) {
			if (newCostEventObjectRef === undefined) {
				throw new Error("objectRef for new cost event is undefined");
			}
			return await CostEventFormApi.createNew({
				costEventItemId: costEvent.itemId,
				description: costEvent.description,
				quantity: costEvent.quantity,
				eventDate: costEvent.eventDate,
				objectRef: newCostEventObjectRef,
				cost: costEvent.cost,
				price: costEvent.price,
				explanation: costEvent.explanation,
			});
		} else {
			await CostEventFormApi.update({ costEvent });
			return costEventId;
		}
	}
};
